import { useTableActionContext } from '@aibees/elephant/context';
import { useAddCampaignMutationApi } from '@aibees/elephant/hooks/integration/use-add-campaign-mutation-api';
import { useInfiniteCampaigns } from '@aibees/elephant/hooks/integration/use-infinite-campaigns';
import { useIntegrationTypeName } from '@aibees/elephant/hooks/integration/use-integration-type-name';
import { IntegrationType } from '@aibees/elephant/types/integration';
import { generateExportName } from '@aibees/elephant/utils';
import {
  Icon,
  IconButton,
  OutlinedInput,
  TextEllipsis,
} from '@aibees/shared/components';
import { useBoolean, useDebounce } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  TextField,
} from '@mui/material';

import { memo, useEffect, useState } from 'react';
import { useIntegrationContext } from '../export-dialog/context/integration-context';
import * as Styled from './styles';
import * as SX from './sx';
import type {
  IntegrationCampaignOption,
  IntegrationSelectCampaignProps as Props,
} from './types';
import { useConvertDataToDropDownOption } from './use-convert-data-to-drop-down-option';

export const IntegrationSelectCampaign = memo(({ className }: Props) => {
  const [inputValue, setInputValue] = useState<string>('');
  const searchKeyDebounced = useDebounce(inputValue, 1_000);
  const { refetch, campaigns, isLoading } =
    useInfiniteCampaigns(searchKeyDebounced);
  const { integrationConfig, setExportName } = useTableActionContext();
  const { selectedIntegration, setSelectedCampaign, selectedCampaign } =
    useIntegrationContext();
  const options = useConvertDataToDropDownOption(campaigns);
  const integrationType = useIntegrationTypeName(integrationConfig?.type);
  const [name, setName] = useState<string>('AI Bess NW - Campaign');

  const state = useBoolean();
  const api = useAddCampaignMutationApi(
    integrationType as IntegrationType,
    selectedIntegration?.id
  );
  const submitButtonTitle = state.value
    ? api.isLoading
      ? 'Saving ...'
      : 'Save'
    : 'Create';

  const handleChange = (e: IntegrationCampaignOption) => {
    setExportName(generateExportName(e?.name));
    setSelectedCampaign(e);
  };

  const handleChangeName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setName(e.target.value);
  };

  const handleClick = () => {
    if (api.isIdle) {
      state.onTrue();
    }
    if (state.value) {
      api
        .mutateAsync({
          name,
        })
        .then((response) => {
          refetch();
          setSelectedCampaign(response as unknown as IntegrationCampaignOption);
        });
    }
  };
  useEffect(() => {
    if (api.isSuccess) {
      state?.onFalse();
    }
  }, [api.isSuccess, state]);

  return (
    <Styled.Wrapper
      className={classnames('IntegrationSelectCampaign', className)}
      sx={SX.root(state.value)}
    >
      {state.value ? (
        <Box flex={1}>
          <OutlinedInput
            fullWidth
            label="Campaign Name"
            placeholder="Enter Campaign Name"
            required
            size="small"
            disabled={api.isLoading}
            value={name}
            onChange={handleChangeName}
          />
        </Box>
      ) : (
        <Autocomplete
          fullWidth
          options={options}
          size="medium"
          value={selectedCampaign}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(_, newValue) => {
            handleChange(newValue as IntegrationCampaignOption);
          }}
          getOptionLabel={(option) => option.name?.toString() || ''}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderOption={(props, option) => (
            <li {...props}>
              <TextEllipsis
                value={option.name}
                variant="body3"
                color="gray.700"
              />
            </li>
          )}
          noOptionsText={
            !campaigns && !isLoading
              ? "You don't have any campaigns yet."
              : (!!campaigns?.length && inputValue && !isLoading) ||
                (!!campaigns?.length && isLoading)
              ? 'Loading campaigns...'
              : campaigns?.length === 0 && !isLoading
              ? 'Campaign Not Found'
              : 'Loading campaigns...'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Existing Campaign"
              autoComplete="off"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Icon
                        name="folder-open-outline"
                        size={16}
                        color={
                          selectedCampaign?.id || inputValue
                            ? 'grey.700'
                            : 'grey.400'
                        }
                      />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      <IconButton
        variant="outlined"
        icon="autorenew"
        iconSize={22}
        iconColor="grey.600"
        size="small"
        disabled={state.value}
        onClick={() => refetch()}
      />

      <Button
        startIcon={!state.value && <Icon name="plus" />}
        variant="outlined"
        size="small"
        disabled={(!name && state.value) || api.isLoading}
        onClick={handleClick}
      >
        {submitButtonTitle}
      </Button>
    </Styled.Wrapper>
  );
});

IntegrationSelectCampaign.displayName = 'IntegrationSelectCampaign';
